/**
 * Pure helpers for `dmb-progress-bar`. Kept framework-free so they can be reused (e.g. for legends) and unit tested.
 *
 * Semantics (taken over from the legacy PHP `ProgressBar` used in the sys monitor pages):
 * - `value < 0`          → error: full bar in the error colour
 * - `value === 0`        → empty: only the track is visible
 * - `0 < value < 1`      → partial: bar width = value, colour by level (low ≤ 0.25 < medium < 0.6 ≤ high)
 * - `value >= 1`         → complete: full bar, colour optionally derived from `relativeValue` (0..2, 1 = normal)
 *
 * Colours come from the theme tokens `--status-*` (see apps/dmb-web/src/styles.scss), so light and dark mode work.
 */

export type ProgressBarKind = 'error' | 'empty' | 'low' | 'medium' | 'high' | 'complete';

export type ProgressBarState = {
  kind: ProgressBarKind;
  /** Fill ratio between 0 and 1 */
  fill: number;
  /** Explicit colour, only set for complete bars with a `relativeValue`; otherwise the theme colour of `kind` is used */
  color: string | null;
};

export const progressBarLevels = { medium: 0.25, high: 0.6 };

export const relativeColors = { from: '#004000', to: '#80ff80' };

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function parseHex(hex: string): [number, number, number] {
  const normalized = hex.replace('#', '');
  const full =
    normalized.length === 3
      ? normalized
          .split('')
          .map((c) => c + c)
          .join('')
      : normalized;
  const int = parseInt(full, 16);
  return [(int >> 16) & 255, (int >> 8) & 255, int & 255];
}

function toHex(rgb: [number, number, number]): string {
  return '#' + rgb.map((c) => Math.round(c).toString(16).padStart(2, '0')).join('');
}

/** Linear interpolation between two hex colours, `ratio` is clamped to [0, 1]. */
export function interpolateColor(from: string, to: string, ratio: number): string {
  const t = clamp(Number.isFinite(ratio) ? ratio : 0, 0, 1);
  const a = parseHex(from);
  const b = parseHex(to);
  return toHex([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t]);
}

export function toProgressBarState(value: number, relativeValue?: number | null): ProgressBarState {
  if (!Number.isFinite(value) || value === 0) {
    return { kind: 'empty', fill: 0, color: null };
  }
  if (value < 0) {
    return { kind: 'error', fill: 1, color: null };
  }
  if (value < 1) {
    const kind = value <= progressBarLevels.medium ? 'low' : value < progressBarLevels.high ? 'medium' : 'high';
    return { kind, fill: value, color: null };
  }
  const color =
    relativeValue === null || relativeValue === undefined
      ? null
      : interpolateColor(relativeColors.from, relativeColors.to, clamp(relativeValue, 0, 2) / 2);
  return { kind: 'complete', fill: 1, color };
}

/** Text for a progress label, e.g. `45%`; `Error` for negative values */
export function progressBarLabel(value: number): string {
  if (!Number.isFinite(value)) {
    return '0%';
  }
  return value < 0 ? 'Error' : `${Math.floor(Math.min(value, 1) * 100)}%`;
}
